import React, { useContext, useState, useRef, useEffect } from 'react';

import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Box,
  List,
  ListItem,
  Text,
  Flex,
  Spinner,
  Switch,
  HStack,
  useToast,
} from '@chakra-ui/react';

import { SearchIcon } from '@chakra-ui/icons';

import { useMutation } from '@tanstack/react-query';

import { AuthContext } from 'contexts/AuthContext';

import { apiSetOnlineStatus } from 'api';

import type { SearchBarProps, SearchResult } from './SearchBar.types';

const SearchBar: React.FC<SearchBarProps> = ({
  onChange,
  results = [],
  isLoading = false,
  onResultClick,
}) => {
  const { user, authDispatch } = useContext(AuthContext);
  const toast = useToast();

  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const searchIconColor = useColorModeValue('gray.700', 'white');
  const inputBgColor = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputTextColor = useColorModeValue('gray.700', 'gray.100');
  const dropdownBgColor = useColorModeValue('white', 'navy.800');
  const dropdownHoverColor = useColorModeValue('gray.100', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const onlineStatusMutation = useMutation({
    mutationFn: ({ userId, isOnline }: { userId: number; isOnline: boolean }) =>
      apiSetOnlineStatus(userId, isOnline),
    onSuccess: (data, variables) => {
      authDispatch({
        type: 'set_online',
        payload: variables.isOnline,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating online status',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onChange(e);
    setShowDropdown(value.length > 0);
  };

  const handleResultClick = (result: SearchResult) => {
    if (onResultClick) {
      onResultClick(result);
    }
    setShowDropdown(false);
  };

  const handleOnlineToggle = () => {
    if (user?.id) {
      onlineStatusMutation.mutate({
        userId: user.id,
        isOnline: !user.is_online,
      });
    }
  };

  return (
    <HStack spacing={4} align="center" ml={2}>
      <Flex align="center">
        <Text fontSize="sm" mr={2} color={inputTextColor}>
          {user.is_online ? 'Online' : 'Offline'}
        </Text>
        <Switch
          isChecked={user.is_online}
          onChange={handleOnlineToggle}
          colorScheme="green"
          size="sm"
          isDisabled={onlineStatusMutation.isPending}
        />
      </Flex>

      <Box position="relative" ref={wrapperRef}>
        <InputGroup w={{ base: '100%', md: '300px' }}>
          <InputLeftElement
            children={
              <IconButton
                aria-label="search"
                bg="inherit"
                borderRadius="inherit"
                _active={{
                  bg: 'inherit',
                  transform: 'none',
                  borderColor: 'transparent',
                }}
                _hover={{
                  background: 'none',
                }}
                _focus={{
                  background: 'none',
                  boxShadow: 'none',
                }}
                icon={<SearchIcon color={searchIconColor} w="15px" h="15px" />}
              />
            }
          />
          <Input
            variant="search"
            fontSize="sm"
            bg={inputBgColor}
            color={inputTextColor}
            fontWeight="500"
            _placeholder={{ color: 'gray.400', fontSize: '14px' }}
            borderRadius="30px"
            placeholder="Search..."
            onChange={handleInputChange}
            value={inputValue}
          />
        </InputGroup>

        {showDropdown && (
          <Box
            position="absolute"
            left="0"
            right="0"
            mt="15px"
            maxH="300px"
            overflowY="auto"
            bg={dropdownBgColor}
            borderRadius="8px"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
            zIndex="dropdown"
            width="380px"
          >
            {isLoading ? (
              <Flex justify="center" align="center" py={4}>
                <Spinner size="sm" mr={2} />
                <Text>Searching...</Text>
              </Flex>
            ) : results.length === 0 && inputValue ? (
              <Box p={4} textAlign="center">
                <Text color="gray.500">No results found</Text>
              </Box>
            ) : (
              <List>
                {results.map((result) => (
                  <ListItem
                    key={result.id}
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: dropdownHoverColor }}
                    onClick={() => handleResultClick(result)}
                  >
                    <Text fontWeight="500">{result.title}</Text>
                    {result.footnote && (
                      <Text fontSize="sm" color="gray.500">
                        {result.footnote}
                      </Text>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Box>
    </HStack>
  );
};

export default SearchBar;
